import { logMismatch, logSyncMetrics, type MismatchLocation } from './monitoring';

export interface SyncedProfile {
  id: string;
  first_name: string;
  email: string;
  access_token: string;
  is_authenticated: boolean;
}

export interface ProfileSyncResult {
  success: boolean;
  profile?: SyncedProfile;
  error?: string;
  correctedId?: boolean; // True if we had to correct a stale ID
}

/**
 * Syncs UserContext state with the database using access_token as source of truth.
 *
 * This function queries the database to find which profile owns the current access_token,
 * ensuring that local state (React + localStorage) matches the actual authenticated profile.
 *
 * @param currentUserId - The userId currently in React state/localStorage (may be stale)
 * @param accessToken - The access_token from localStorage (source of truth)
 * @param location - Where this sync is being called from (for monitoring)
 * @returns ProfileSyncResult with the verified profile or error details
 */
export async function syncProfileFromToken(
  currentUserId: string | null,
  accessToken: string | null,
  location: MismatchLocation = 'UserContext:bootstrap'
): Promise<ProfileSyncResult> {
  const startTime = Date.now();

  // No token = new anonymous user, nothing to sync
  if (!accessToken) {
    console.log('🔄 profileSync: No access token found, treating as new anonymous user');
    logSyncMetrics(location, startTime, {
      success: false,
      errorCode: 'NO_TOKEN',
      hadMismatch: false,
    });
    return {
      success: false,
      error: 'NO_TOKEN'
    };
  }

  try {
    console.log('🔄 profileSync: Querying server API for profile with access_token...');
    console.log('🔍 profileSync: Current userId in state:', currentUserId);

    // Query via API endpoint (uses service role to bypass RLS restrictions)
    // The anon role cannot query by access_token directly due to RLS security
    const response = await fetch(`/api/get-profile-by-token?token=${encodeURIComponent(accessToken)}`);
    const result = await response.json();

    if (!result.success || !result.data) {
      const errorMsg = result.error || 'Unknown error';

      // 404 means token not found (equivalent to PGRST116)
      if (response.status === 404) {
        console.warn('⚠️ profileSync: Token not found in database, treating as expired/invalid');
        logSyncMetrics(location, startTime, {
          success: false,
          errorCode: 'TOKEN_NOT_FOUND',
          hadMismatch: false,
        });
        return {
          success: false,
          error: 'TOKEN_NOT_FOUND'
        };
      }

      // Other API errors
      console.error('❌ profileSync: API error:', errorMsg);
      logSyncMetrics(location, startTime, {
        success: false,
        errorCode: 'API_ERROR',
        hadMismatch: false,
      });
      return {
        success: false,
        error: `API_ERROR: ${errorMsg}`
      };
    }

    const profile = result.data;

    if (!profile) {
      console.warn('⚠️ profileSync: No profile returned for access_token');
      logSyncMetrics(location, startTime, {
        success: false,
        errorCode: 'NO_PROFILE',
        hadMismatch: false,
      });
      return {
        success: false,
        error: 'NO_PROFILE'
      };
    }

    // Check if we need to correct a stale ID
    const needsCorrection = currentUserId && currentUserId !== profile.id;

    if (needsCorrection) {
      console.warn('🔧 profileSync: ID MISMATCH DETECTED!');
      console.warn('   Stale ID in state:', currentUserId);
      console.warn('   Correct ID from DB:', profile.id);
      console.warn('   Correcting local state to match database...');

      // Log mismatch event for monitoring
      logMismatch(location, currentUserId, profile.id, {
        severity: 'warning',
        wasCorrected: true,
        correctionMethod: 'syncProfileFromToken',
      });
    } else {
      console.log('✅ profileSync: ID verification passed, state matches database');
      console.log('   Verified ID:', profile.id);
    }

    // Log successful sync metrics
    logSyncMetrics(location, startTime, {
      success: true,
      hadMismatch: needsCorrection,
    });

    return {
      success: true,
      profile: profile as SyncedProfile,
      correctedId: needsCorrection
    };

  } catch (err) {
    console.error('❌ profileSync: Unexpected error during sync:', err);
    logSyncMetrics(location, startTime, {
      success: false,
      errorCode: err instanceof Error ? err.message : 'UNKNOWN_ERROR',
      hadMismatch: false,
    });
    return {
      success: false,
      error: err instanceof Error ? err.message : 'UNKNOWN_ERROR'
    };
  }
}

/**
 * Clears stale session data from localStorage when userId changes.
 * Should be called whenever syncProfileFromToken corrects a stale ID.
 */
export function clearStaleSessionData() {
  if (typeof window === 'undefined') return;

  const clearedItems: string[] = [];

  // Clear session ID - new profile needs fresh session
  const sessionId = localStorage.getItem('cubetest-sessionId');
  if (sessionId) {
    localStorage.removeItem('cubetest-sessionId');
    clearedItems.push('sessionId');
  }

  if (clearedItems.length > 0) {
    console.log('🧹 profileSync: Cleared stale session data:', clearedItems.join(', '));
  }
}
